import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useResourceWatch from '../../hooks/useResourceWatch';
import { catalogAPI, clustersAPI } from '../../services/api';

/**
 * Live rollout progress for a release being deployed.
 *
 * Before this, clicking Deploy showed "Starting…" and then nothing until an 8-second poll
 * happened to notice the release. A Patroni cluster is 24 objects and roughly three minutes of
 * that silence; MongoDB is 11 pods and about five. One word at the end ("running" or "failed")
 * was the entire feedback.
 *
 * Uses the same Steve watch that makes the Pods page live at ~0.3s, scoped to the release's
 * namespace. Pods appear as Kubernetes creates them and flip to ready in place — so what you
 * see is the cluster forming, not a spinner.
 *
 * It cannot know the expected pod count up front: the manifests are generated server-side and
 * applied in order, so pods arrive over time. It therefore reports "N ready of M seen" rather
 * than inventing a denominator that would be wrong for the first minute.
 */
const S = {
  wrap: {
    border: '1px solid var(--border)', borderRadius: 12, padding: 18, marginTop: 18,
    background: 'var(--surface)',
  },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          flexWrap: 'wrap', marginBottom: 4 },
  title: { fontSize: 14, fontWeight: 700 },
  sub: { fontSize: 12, color: 'var(--muted)' },
  barOuter: { height: 6, borderRadius: 4, background: 'var(--surface-2)', overflow: 'hidden',
              margin: '14px 0 4px' },
  barInner: (pct, colour) => ({ height: '100%', width: `${pct}%`, background: colour,
                                transition: 'width .35s ease' }),
  list: { maxHeight: 230, overflowY: 'auto', marginTop: 12 },
  row: { display: 'flex', alignItems: 'center', gap: 9, padding: '5px 0', fontSize: 12.5,
         borderBottom: '1px solid var(--surface-2)' },
  dot: (c) => ({ width: 7, height: 7, borderRadius: '50%', background: c, flexShrink: 0 }),
  name: { fontFamily: 'monospace', fontSize: 11.5, flex: 1, wordBreak: 'break-all' },
  state: (c) => ({ fontSize: 11, color: c, fontWeight: 600, whiteSpace: 'nowrap' }),
  live: (on) => ({ fontSize: 11, fontWeight: 600,
                   color: on ? 'var(--color-success-500)' : 'var(--muted-2)' }),
};

// A pod is only useful when READY; Running with a failing probe still serves no traffic.
function podTone(p) {
  if (p.ready) return ['var(--color-success-500)', 'ready'];
  const s = String(p.status || '').toLowerCase();
  if (s.includes('crash') || s.includes('error') || s === 'failed')
    return ['var(--color-danger-500)', p.status];
  if (s.includes('init') || s.includes('creating') || s === 'pending')
    return ['var(--color-warning-500)', p.status];
  return ['var(--color-warning-500)', p.status || 'starting'];
}

export default function DeployProgress({ clusterId, namespace, releaseName, onDone }) {
  const [pods, setPods] = useState({});          // "ns/name" -> {name,status,ready}
  const [seeded, setSeeded] = useState(false);
  // Bootstrap progress lives in the database, not in Kubernetes, so the pod watch cannot see
  // it. Polled only while this panel is mounted — a rollout, not a steady state.
  const [boot, setBoot] = useState(null);

  // Seed from a normal fetch so pods created before the socket opened are not missed — the
  // watch only reports changes from the moment it connects.
  useEffect(() => {
    if (!clusterId || !namespace) return;
    let cancelled = false;
    clustersAPI.getPodsPaged(clusterId, { pageSize: 200, namespace })
      .then(({ data }) => {
        if (cancelled) return;
        const next = {};
        (data.items || []).forEach(p => {
          next[`${p.namespace}/${p.name}`] = {
            name: p.name, status: p.status,
            ready: String(p.ready || '').split('/').every((n, i, a) => a[0] === a[1] && a[0] !== '0'),
          };
        });
        setPods(next);
        setSeeded(true);
      })
      .catch(() => setSeeded(true));
    return () => { cancelled = true; };
  }, [clusterId, namespace]);

  const upsert = useCallback((key, flat) => {
    setPods(prev => ({
      ...prev,
      [key]: {
        name: flat.name,
        status: flat.status,
        // `ready` arrives as "1/1" — every container must be up, and "0/0" is not ready.
        ready: String(flat.ready || '').split('/').every((n, i, a) => a[0] === a[1] && a[0] !== '0'),
      },
    }));
  }, []);

  const remove = useCallback((key) => {
    setPods(prev => { const n = { ...prev }; delete n[key]; return n; });
  }, []);

  const { connected } = useResourceWatch(clusterId, 'pods', {
    enabled: !!clusterId && !!namespace,
    namespace,
    onPatch: upsert,
    onNew: upsert,
    onRemove: remove,
  });

  // 2s is ample: bootstrap steps take 5-20s each. Stops as soon as the release settles, so
  // there is no background polling once a deploy is done.
  useEffect(() => {
    if (!clusterId) return undefined;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await catalogAPI.releases(clusterId);
        const mine = (data.releases || []).find(r => r.name === releaseName);
        if (!stop) setBoot(mine || null);
        // A settled release needs no further polling.
        if (mine && ['running', 'deployed', 'failed'].includes(String(mine.status).toLowerCase())) {
          clearInterval(iv);
        }
      } catch { /* transient — the next tick retries */ }
    };
    tick();
    const iv = setInterval(tick, 2000);
    return () => { stop = true; clearInterval(iv); };
  }, [clusterId, releaseName]);

  const list = useMemo(
    () => Object.values(pods).sort((a, b) => a.name.localeCompare(b.name)), [pods]);
  const ready = list.filter(p => p.ready).length;
  const failed = list.filter(p => podTone(p)[1] !== 'ready'
    && String(p.status || '').toLowerCase().match(/crash|error|failed/)).length;
  const total = list.length;
  const pct = total ? Math.round((ready / total) * 100) : 0;
  const allReady = total > 0 && ready === total;

  // Derived from the release row, not from pods: a sharded MongoDB has every pod ready while
  // rs.initiate, createUser and two addShard calls are still going.
  const relStatus = String(boot?.status || '').toLowerCase();
  const bootstrapping = boot?.phase === 'bootstrapping';
  const settled = ['running', 'deployed'].includes(relStatus);
  const relFailed = relStatus === 'failed';
  const stepsDone = boot?.steps_done || [];
  const stepsTotal = boot?.steps_total || 0;

  // Only when the RELEASE settles — pods ready is not the finish line for a clustered DB.
  useEffect(() => {
    if ((settled || relFailed) && onDone) onDone();
  }, [settled, relFailed, onDone]);

  const barColour = failed || relFailed ? 'var(--color-danger-500)'
    : settled ? 'var(--color-success-500)' : 'var(--brand)';

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <div>
          <div style={S.title}>
            {relFailed ? 'Deploy failed —' : settled ? 'Deployed' : 'Deploying'} {releaseName}
          </div>
          <div style={S.sub}>
            namespace <code>{namespace}</code>
            {failed > 0 && (
              <> · <span style={{ color: 'var(--color-danger-500)', fontWeight: 600 }}>
                {failed} failing
              </span></>
            )}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            {ready}<span style={{ color: 'var(--muted-2)', fontWeight: 400 }}> / {total || '—'}</span>
          </div>
          {/* "seen", not "expected": manifests apply in order, so pods keep arriving and any
              fixed denominator would be wrong for the first minute. */}
          <div style={{ fontSize: 10.5, color: 'var(--muted-2)' }}>pods ready of seen</div>
        </div>
      </div>

      <div style={S.barOuter}>
        <div style={S.barInner(pct, barColour)} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
        <span style={S.live(connected)}>{connected ? '● live' : '○ connecting…'}</span>
        <span style={{ color: 'var(--muted-2)' }}>
          {settled ? 'cluster ready'
            : relFailed ? 'see the reason below'
            : bootstrapping ? 'pods up — initialising the cluster'
            : allReady ? 'all pods ready'
            : !seeded ? 'reading current state…'
            : total === 0 ? 'waiting for the first pod…'
            : 'pods are still starting'}
        </span>
      </div>

      {/* Phase two. Applying manifests only gets pods running; a clustered database is not
          usable until rs.initiate / addShard / createUser have run, and that is invisible to
          a pod watch because it happens over mongosh, not in Kubernetes. */}
      {(bootstrapping || stepsDone.length > 0) && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--surface-2)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                        marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                           letterSpacing: 0.5, color: 'var(--muted)' }}>
              Cluster initialisation
            </span>
            {stepsTotal > 0 && (
              <span style={{ fontSize: 11, color: 'var(--muted-2)' }}>
                step {boot?.step ?? stepsDone.length} of {stepsTotal}
              </span>
            )}
          </div>

          {/* Completed steps stay listed, so a mid-sequence failure shows how far it got —
              "the replica sets formed, shard registration broke" is the useful distinction. */}
          {stepsDone.map((label) => (
            <div key={label} style={{ ...S.row, borderBottom: 'none', padding: '3px 0' }}>
              <span style={{ color: 'var(--color-success-500)', fontSize: 12 }}>✓</span>
              <span style={{ flex: 1, color: 'var(--muted)' }}>{label}</span>
            </div>
          ))}

          {bootstrapping && boot?.step_label && (
            <div style={{ ...S.row, borderBottom: 'none', padding: '3px 0' }}>
              <span style={S.dot(boot.step_state === 'failed'
                ? 'var(--color-danger-500)' : 'var(--color-warning-500)')} />
              <span style={{ flex: 1, fontWeight: 600 }}>{boot.step_label}</span>
              <span style={S.state(boot.step_state === 'failed'
                ? 'var(--color-danger-500)' : 'var(--color-warning-500)')}>
                {boot.step_state === 'failed' ? 'failed' : 'running…'}
              </span>
            </div>
          )}

          {relFailed && boot?.failure_detail && (
            <div style={{ marginTop: 8, padding: '9px 11px', borderRadius: 8, fontSize: 11.5,
              background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.35)',
              fontFamily: 'monospace', lineHeight: 1.5, wordBreak: 'break-word' }}>
              {boot.failure_detail}
            </div>
          )}
        </div>
      )}

      <div style={S.list}>
        {list.map(p => {
          const [colour, label] = podTone(p);
          return (
            <div key={p.name} style={S.row}>
              <span style={S.dot(colour)} />
              <span style={S.name}>{p.name}</span>
              <span style={S.state(colour)}>{label}</span>
            </div>
          );
        })}
        {total === 0 && seeded && (
          <div style={{ fontSize: 12, color: 'var(--muted-2)', padding: '10px 0' }}>
            No pods yet — manifests are still being applied.
          </div>
        )}
      </div>
    </div>
  );
}
